import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch, type Ref } from "vue";
import { DS_TOOLTIP_PLACEMENTS, type DSTooltipOptions } from "@/src/design_system/primitives/tooltip/types/options";

interface DSInputElementProvider {
  getInputElement: () => HTMLInputElement | HTMLTextAreaElement | undefined;
}

interface UseDSUrlFieldTooltipOptions {
  inputRef: Ref<DSInputElementProvider | undefined>;
  value: Ref<string | null | undefined>;
  disabled: Ref<boolean>;
}

export const useDSUrlFieldTooltip = ({ inputRef, value, disabled }: UseDSUrlFieldTooltipOptions) => {
  const isTruncated = ref(false);
  let observedInput: HTMLInputElement | HTMLTextAreaElement | undefined;
  let resizeObserver: ResizeObserver | undefined;

  const observeInput = (input: HTMLInputElement | HTMLTextAreaElement | undefined) => {
    if (input === observedInput) {
      return;
    }

    resizeObserver?.disconnect();
    observedInput = input;

    if (input) {
      resizeObserver?.observe(input);
    }
  };

  const refresh = async () => {
    await nextTick();

    const input = inputRef.value?.getInputElement();
    observeInput(input);
    isTruncated.value = Boolean(!disabled.value && input && input.scrollWidth > input.clientWidth);
  };

  onMounted(() => {
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => void refresh());
    }

    void refresh();
  });

  watch([inputRef, value, disabled], () => void refresh(), { flush: "post" });

  onBeforeUnmount(() => {
    resizeObserver?.disconnect();
  });

  const tooltip = computed<DSTooltipOptions | false>(() => {
    const text = value.value ?? "";

    if (disabled.value || !text || !isTruncated.value) {
      return false;
    }

    return {
      text,
      placement: DS_TOOLTIP_PLACEMENTS.Top,
    };
  });

  return {
    tooltip,
    refresh,
  };
};
